import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const JobCard = ({ job, onDragStart, onContextMenu }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'var(--color-error)';
      case 'high':
        return 'var(--color-warning)';
      case 'medium':
        return 'var(--color-accent)';
      case 'low':
        return 'var(--color-success)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getTradeIcon = (trade) => {
    const icons = {
      'Plumbing': 'Droplet',
      'Electrical': 'Zap',
      'Heating': 'Flame',
      'Carpentry': 'Hammer',
      'Painting': 'Paintbrush',
      'Roofing': 'Home',
      'Glazing': 'Square',
      'General': 'Wrench'
    };
    return icons?.[trade] || 'Wrench';
  };

  const calculateElapsedTime = (createdDate) => {
    const now = new Date();
    const created = new Date(createdDate);
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    return `${diffHours}h`;
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, job)}
      onContextMenu={(e) => onContextMenu(e, job)}
      className="bg-surface rounded-lg p-4 border border-border cursor-move transition-smooth hover:shadow-warm-md hover:border-accent mb-3"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div 
            className="h-8 w-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${getPriorityColor(job?.priority)}20` }}
          >
            <Icon name={getTradeIcon(job?.trade)} size={16} color={getPriorityColor(job?.priority)} />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground font-data">{job?.id}</p>
            <p className="text-xs text-muted-foreground font-caption">{job?.trade}</p>
          </div>
        </div>
        <div 
          className="h-6 px-2 rounded flex items-center justify-center"
          style={{ backgroundColor: `${getPriorityColor(job?.priority)}20` }}
        >
          <span className="text-xs font-medium font-caption" style={{ color: getPriorityColor(job?.priority) }}>
            {job?.priority?.toUpperCase()}
          </span>
        </div>
      </div>
      <div className="mb-3">
        <p className="text-sm font-medium text-foreground mb-1">{job?.title}</p>
        <p className="text-xs text-muted-foreground line-clamp-2">{job?.description}</p>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
        <p className="text-xs text-muted-foreground">{job?.propertyAddress}</p>
      </div>
      {job?.contractor && (
        <div className="flex items-center gap-2 mb-3 p-2 rounded bg-input">
          <Image 
            src={job?.contractorAvatar} 
            alt={job?.contractorAvatarAlt}
            className="w-6 h-6 rounded-full object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground truncate">{job?.contractor}</p>
            <div className="flex items-center gap-1">
              <Icon name="Star" size={12} color="var(--color-warning)" />
              <span className="text-xs text-muted-foreground font-data">{job?.contractorRating}</span>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-1">
          <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
          <span className="text-xs text-muted-foreground font-data">{calculateElapsedTime(job?.createdDate)}</span>
        </div>
        {job?.hasAttachments && (
          <div className="flex items-center gap-1">
            <Icon name="Paperclip" size={14} color="var(--color-accent)" />
            <span className="text-xs text-accent font-data">{job?.attachmentCount}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCard;